import React, {
    HtmlHTMLAttributes,
    useState,
    useRef,
    useEffect,
    useMemo,
} from 'react'

import { Theme } from '@material-ui/core'
import {
    ArrowUpward,
    ArrowDownward,
    CheckCircle,
    Error,
    HourglassEmpty,
    WarningRounded,
    Block,
    InfoOutlined,
    CheckCircleOutline,
    Close,
} from '@material-ui/icons'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import TableChartIcon from '@material-ui/icons/TableChart'
import Tooltip from '@material-ui/core/Tooltip'

import { Container } from '../../../components'
import {
    ResidentTermDirectoryRow,
    CriteriaGroup,
    DeliverableValueStatus,
    EntityClassDataType,
    DeliverableValue,
    ResidentTermDirectoryStatus,
    EntityClass,
    MergeRequest,
} from '../../../models'
import {
    DeliverableValueStatusConfig,
    getEntityClassDisplayName,
} from '../../../models/DocumentAudit/services'
import '../../../styles/LoadingAnimation.css'
import { axiosInstance } from '../../../helpers'
import { ColumnDetailPanel } from './'
import { useLeaseAudit } from '../../../hooks/documentAudit/useLeaseAudit'

// Constants for styling and layout
const STATUS_COLUMN_WIDTH = 90
const CELL_STYLES = {
    base: {
        padding: '12px 16px',
        flex: 1,
        minWidth: 175,
        maxWidth: 350,
        height: '100%',
        alignItems: 'center' as const,
        justifyContent: 'flex-start' as const,
        borderRight: '1px solid #e0e0e0',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
        position: 'relative' as const,
    },
    header: {
        fontWeight: 600,
    },
    missingRentRoll: {
        borderLeft: '4px solid',
        borderLeftColor: 'error.light',
        backgroundColor: 'rgba(255, 235, 235, 0.5)',
    },
    missingDocument: {
        borderLeft: '4px solid',
        borderLeftColor: 'warning.light',
        backgroundColor: 'rgba(255, 244, 230, 0.5)',
    },
}

// Types for props
interface Props extends HtmlHTMLAttributes<HTMLDivElement> {
    paginatedRows: ResidentTermDirectoryRow[]
    allRows?: ResidentTermDirectoryRow[]
    theme: Theme
    criteriaGroup?: CriteriaGroup
    onClickCell: (cell: DeliverableValue, row: ResidentTermDirectoryRow) => void
    onSort: (entityClassId: number) => void
    sortConfig: {
        key: string
        direction: 'asc' | 'desc' | null
    }
    isLoading?: boolean
    entityClassifications?: EntityClass[]
    leaseAuditId: number
    bulkAcceptDeliverableValues: (
        deliverableValueIds: number[],
        notes: string,
    ) => Promise<void>
    mergeResidentTermDirectories: (params: MergeRequest) => Promise<void>
    isMerging: boolean
}

// ===== Main Components =====

// Header Cell Component
const HeaderCell = ({
    theme,
    label,
    sortKey,
    currentSortKey,
    sortDirection,
    onSort,
    onOpenDetail,
    style = {},
}: {
    theme: Theme
    label: string
    sortKey?: number
    currentSortKey?: string
    sortDirection?: 'asc' | 'desc' | null
    onSort?: (entityClassId: number) => void
    onOpenDetail?: (columnLabel: string, columnKey?: number) => void
    style?: React.CSSProperties
}) => {
    const isStatus = !sortKey
    const isSorted = sortKey && currentSortKey === sortKey.toString()

    const handleSort = (e: React.MouseEvent) => {
        if (sortKey && onSort) {
            onSort(sortKey)
        }
    }

    const handleOpenDetail = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onOpenDetail) {
            onOpenDetail(label, sortKey)
        }
    }

    return (
        <Container
            style={{
                ...CELL_STYLES.base,
                ...CELL_STYLES.header,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
                ...(isStatus
                    ? {
                          minWidth: STATUS_COLUMN_WIDTH,
                          maxWidth: STATUS_COLUMN_WIDTH,
                          justifyContent: 'center',
                      }
                    : {
                          cursor: 'pointer',
                      }),
                ...style,
            }}
            onClick={handleSort}
        >
            <Container
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Container
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <span>{label}</span>
                    {isSorted && sortDirection === 'asc' && (
                        <ArrowUpward style={{ fontSize: 16 }} />
                    )}
                    {isSorted && sortDirection === 'desc' && (
                        <ArrowDownward style={{ fontSize: 16 }} />
                    )}
                </Container>
                {onOpenDetail && (
                    <div
                        onClick={handleOpenDetail}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <InfoOutlined style={{ fontSize: 16 }} />
                    </div>
                )}
            </Container>
        </Container>
    )
}

// Data Cell Component
const DataCell = ({
    children,
    theme,
    status,
    messageCount,
    value,
    onClick,
    style = {},
}: {
    children: React.ReactNode
    theme: Theme
    status?: DeliverableValueStatus
    messageCount?: number
    value?: DeliverableValue
    onClick?: () => void
    style?: React.CSSProperties
}) => {
    const statusConfig = status && DeliverableValueStatusConfig[status]
    const StatusIcon = statusConfig?.icon.component

    // Status types
    const isMatched = status === ('MATCHED' as DeliverableValueStatus)
    const isNotMatched = status === ('NOT_MATCHED' as DeliverableValueStatus)
    const isMissingRentRoll =
        status === ('MISSING_RENT_ROLL' as DeliverableValueStatus)
    const isMissingDocument =
        status === ('MISSING_DOCUMENT' as DeliverableValueStatus)
    const isAccepted = status === ('ACCEPTED' as DeliverableValueStatus)
    const isRentRollOnly =
        status === ('RENT_ROLL_ONLY' as DeliverableValueStatus)

    // Build cell style based on data conditions
    let cellStyle: React.CSSProperties = {
        ...CELL_STYLES.base,
        backgroundColor: statusConfig?.backgroundColor,
        cursor: onClick ? 'pointer' : 'default',
        ...style,
    }

    if (isMissingRentRoll) {
        cellStyle = {
            ...cellStyle,
            borderLeft: `4px solid ${theme.palette.warning.light}`,
            backgroundColor: 'rgba(255, 253, 231, 0.5)', // Light yellow
        }
    } else if (isMissingDocument) {
        cellStyle = {
            ...cellStyle,
            borderLeft: `4px solid ${theme.palette.warning.main}`,
            backgroundColor: 'rgba(255, 248, 225, 0.5)', // Slightly darker yellow
        }
    } else if (isNotMatched) {
        // Make NOT_MATCHED status red
        cellStyle = {
            ...cellStyle,
            borderLeft: `4px solid ${theme.palette.error.main}`,
            backgroundColor: 'rgba(255, 235, 235, 0.5)', // Light red
        }
    } else if (isAccepted) {
        // Make ACCEPTED status green with a different shade
        cellStyle = {
            ...cellStyle,
            borderLeft: `4px solid ${theme.palette.success.dark}`,
            backgroundColor: 'rgba(232, 245, 233, 0.5)', // Light green
        }
    } else if (isRentRollOnly) {
        cellStyle = {
            ...cellStyle,
            backgroundColor: theme.palette.background.paper,
        }
    }

    return (
        <Container onClick={onClick} style={cellStyle}>
            <Container
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                {/* Cell value content */}
                <div
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        paddingRight: '40px', // Space for indicators
                    }}
                >
                    {children}
                </div>

                {/* Indicators column */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'absolute' as const,
                        right: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        gap: '2px',
                    }}
                >
                    {/* 1. MATCHED: Show only the green checkmark circle */}
                    {isMatched && StatusIcon && (
                        <StatusIcon
                            style={{
                                fontSize: 16,
                                color: statusConfig.icon.color,
                            }}
                        />
                    )}

                    {/* 2. NOT_MATCHED: Show only the red exclamation icon */}
                    {isNotMatched && StatusIcon && (
                        <StatusIcon
                            style={{
                                fontSize: 16,
                                color: statusConfig.icon.color,
                            }}
                        />
                    )}

                    {/* 3. MISSING_RENT_ROLL: Show PDF icon (what's present) with RR text */}
                    {isMissingRentRoll && (
                        <>
                            <PictureAsPdfIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.warning.dark,
                                }}
                            />
                            <span
                                style={{
                                    color: theme.palette.warning.dark,
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    marginTop: '2px',
                                }}
                            >
                                DOC
                            </span>
                        </>
                    )}

                    {/* 4. MISSING_DOCUMENT: Show Excel/Table icon (what's present) with DOC text */}
                    {isMissingDocument && (
                        <>
                            <TableChartIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.warning.dark,
                                }}
                            />
                            <span
                                style={{
                                    color: theme.palette.warning.dark,
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    marginTop: '2px',
                                }}
                            >
                                RR
                            </span>
                        </>
                    )}

                    {/* 5. ACCEPTED: Show the VerifiedUser icon */}
                    {isAccepted && StatusIcon && (
                        <StatusIcon
                            style={{
                                fontSize: 16,
                                color: statusConfig.icon.color,
                            }}
                        />
                    )}

                    {/* 6. RENT_ROLL_ONLY: Show the VerifiedUser icon
                    {isRentRollOnly && StatusIcon && (
                        <StatusIcon
                            style={{
                                fontSize: 16,
                                color: statusConfig.icon.color,
                            }}
                        />
                    )} */}

                    {/* Message count badge */}
                    {messageCount !== undefined && messageCount > 0 && (
                        <div
                            style={{
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                borderRadius: '12px',
                                padding: '1px 4px',
                                fontSize: '10px',
                                lineHeight: '1',
                                marginTop: '2px',
                            }}
                        >
                            {messageCount}
                        </div>
                    )}
                </div>
            </Container>
        </Container>
    )
}

// Row status badge component
const RowStatusBadge = ({
    status,
    theme,
}: {
    status: ResidentTermDirectoryStatus
    theme: Theme
}) => {
    // Configuration for row status badges
    const statusConfig = {
        INITIAL: {
            color: theme.palette.grey[700],
            borderColor: theme.palette.grey[500],
            label: 'Initial',
            icon: <HourglassEmpty />,
        },
        MATCHED: {
            color: theme.palette.success.dark,
            borderColor: theme.palette.success.main,
            label: 'Matched',
            icon: <CheckCircle />,
        },
        PARTIALLY_MATCHED: {
            color: theme.palette.error.dark,
            borderColor: theme.palette.error.main,
            label: 'Mismatched',
            icon: <WarningRounded />,
        },
        MISSING_RENT_ROLL: {
            color: theme.palette.warning.dark,
            borderColor: theme.palette.warning.main,
            label: 'PDF Document Only',
            icon: <PictureAsPdfIcon />,
        },
        MISSING_LEASE: {
            color: theme.palette.warning.dark,
            borderColor: theme.palette.warning.main,
            label: 'Excel/Rent Roll Only',
            icon: <TableChartIcon />,
        },
    }

    const config = statusConfig[status] || statusConfig['INITIAL']

    return (
        <Tooltip title={config.label} placement="top">
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    border: `2px solid ${config.borderColor}`,
                    color: config.color,
                    padding: 0,
                    lineHeight: 0,
                }}
            >
                {React.cloneElement(config.icon, {
                    style: {
                        fontSize: 20,
                    },
                })}
            </div>
        </Tooltip>
    )
}

// Function to mark a value as good
const markValueAsGood = async (valueId: number): Promise<DeliverableValue> => {
    const response = await axiosInstance.patch<DeliverableValue>(
        `/document-audit/deliverable-value/${valueId}/`,
        {
            status: 'MATCH_FIXED',
        },
    )
    return response.data
}

// Main table component
export const AuditTable = ({
    paginatedRows = [],
    allRows,
    theme,
    criteriaGroup,
    onSort,
    sortConfig,
    isLoading = false,
    onClickCell,
    entityClassifications = [],
    leaseAuditId,
    bulkAcceptDeliverableValues,
    mergeResidentTermDirectories,
    isMerging,
    ...props
}: Props) => {
    // Add leaseAudit hook
    const leaseAuditController = useLeaseAudit()

    // State for detail panel
    const [detailPanel, setDetailPanel] = useState<{
        open: boolean
        columnLabel: string
        columnKey?: number
    }>({
        open: false,
        columnLabel: '',
    })

    // Reference to the table container for positioning the detail panel
    const tableRef = useRef<HTMLDivElement>(null)
    const [tableRect, setTableRect] = useState({
        top: 0,
        left: 0,
        width: 0,
        height: 0,
    })

    useEffect(() => {
        if (tableRef.current) {
            const rect = tableRef.current.getBoundingClientRect()
            setTableRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
            })
        }

        const handleResize = () => {
            if (tableRef.current) {
                const rect = tableRef.current.getBoundingClientRect()
                setTableRect({
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                })
            }
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const handleOpenDetail = (columnLabel: string, columnKey?: number) => {
        setDetailPanel({
            open: true,
            columnLabel,
            columnKey,
        })
    }

    const handleCloseDetail = () => {
        setDetailPanel((prev) => ({
            ...prev,
            open: false,
        }))
    }

    // Function to mark a value as good
    const handleMarkAsGood = async (valueId: number) => {
        try {
            // Call the API to mark the value as good
            await markValueAsGood(valueId)

            // Update the local state to reflect the change immediately
            const updatedRows = paginatedRows.map((row) => {
                const updatedValues = row.values.map((value) => {
                    if (value.id === valueId) {
                        return {
                            ...value,
                            status: 'MATCH_FIXED' as DeliverableValueStatus,
                        }
                    }
                    return value
                })

                return {
                    ...row,
                    values: updatedValues,
                }
            })

            // Close the panel to reflect the changes
            handleCloseDetail()

            // You'd typically dispatch an action to update the state in a real app
            // For demo purposes, we're just updating the local state, but in a real app
            // you would likely invalidate the query or update the Redux store
        } catch (error) {
            console.error('Error marking value as good:', error)
            // You may want to show an error notification here
        }
    }

    // Function to mark multiple values as good
    const handleMarkMultipleAsGood = async (valueIds: number[]) => {
        try {
            // Process all the values
            for (const valueId of valueIds) {
                await markValueAsGood(valueId)
            }

            // Update the local state to reflect the changes
            // In a real application, you would invalidate the query cache or dispatch actions

            // Close the panel to reflect the changes
            handleCloseDetail()
        } catch (error) {
            console.error('Error marking values as good:', error)
        }
    }

    // Convert entityClassifications to ColumnInfo format with user-friendly names
    const availableColumns: { key: number; label: string }[] = []
    criteriaGroup?.match_criteria.map((criteria) => {
        if (criteria.display) {
            availableColumns.push({
                key: criteria.entity_class.id,
                label: getEntityClassDisplayName(criteria.entity_class.name),
            })
        }
    })

    // Add function to handle column change
    const handleColumnChange = (columnKey: number) => {
        const column = entityClassifications.find((c) => c.id === columnKey)
        if (column) {
            setDetailPanel({
                open: true,
                columnLabel: getEntityClassDisplayName(column.name),
                columnKey: column.id,
            })
        }
    }
    console.log('paginatedRows', paginatedRows)

    return (
        <Container {...props} ref={tableRef}>
            {/* Table header */}
            <Container
                style={{
                    flexDirection: 'row',
                    height: 60,
                    borderBottom: '4px solid #e0e0e0',
                    backgroundColor: theme.palette.background.paper,
                    position: 'sticky' as const,
                    top: 0,
                    zIndex: 1,
                    width: '100%',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                }}
            >
                {/* Status column header */}
                <HeaderCell
                    theme={theme}
                    label="Status"
                    style={{
                        minWidth: STATUS_COLUMN_WIDTH,
                        maxWidth: STATUS_COLUMN_WIDTH,
                        justifyContent: 'center',
                    }}
                    onOpenDetail={handleOpenDetail}
                />

                {/* Required criteria headers */}
                {criteriaGroup?.match_criteria
                    .filter((criteria) => criteria.display)
                    .map((criteria) => (
                        <HeaderCell
                            key={`header-${criteria.entity_class.id}`}
                            theme={theme}
                            label={getEntityClassDisplayName(
                                criteria.entity_class.name,
                            )}
                            sortKey={criteria.entity_class.id}
                            currentSortKey={sortConfig.key}
                            sortDirection={sortConfig.direction}
                            onSort={onSort}
                            onOpenDetail={handleOpenDetail}
                        />
                    ))}
            </Container>

            {/* Detail Panel */}
            <ColumnDetailPanel
                open={detailPanel.open}
                onClose={handleCloseDetail}
                columnLabel={detailPanel.columnLabel}
                columnKey={detailPanel.columnKey}
                tableRect={tableRect}
                theme={theme}
                rows={allRows || paginatedRows}
                onMarkAsGood={handleMarkAsGood}
                onMarkMultipleAsGood={handleMarkMultipleAsGood}
                availableColumns={availableColumns}
                onColumnChange={handleColumnChange}
                onViewDetails={onClickCell}
                leaseAuditId={leaseAuditId}
                bulkAcceptDeliverableValues={bulkAcceptDeliverableValues}
                mergeResidentTermDirectories={mergeResidentTermDirectories}
                isMerging={isMerging}
            />

            {/* Table body */}
            {isLoading ? (
                <Container
                    style={{
                        padding: '20px 0',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <div className="ezos-loader">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </Container>
            ) : paginatedRows.length === 0 ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px',
                    }}
                >
                    <div>No data found</div>
                </div>
            ) : (
                paginatedRows.map((row) => (
                    <TableRowWithAlwaysDisplayIds
                        key={row.id}
                        row={row}
                        theme={theme}
                        criteriaGroup={criteriaGroup}
                        onClickCell={onClickCell}
                    />
                ))
            )}
        </Container>
    )
}

// Modified Table row component that accepts alwaysDisplayEntityClassIds
const TableRowWithAlwaysDisplayIds = ({
    row,
    theme,
    criteriaGroup,
    onClickCell,
}: {
    row: ResidentTermDirectoryRow
    theme: Theme
    criteriaGroup: CriteriaGroup | undefined
    onClickCell: (cell: DeliverableValue, row: ResidentTermDirectoryRow) => void
}) => {
    // Helper function to format cell values based on data type
    const formatValueByDataType = (
        value: string | null,
        dataType: EntityClassDataType,
    ): string => {
        if (!value) return '—'

        switch (dataType) {
            case 'DATE':
                return new Date(value).toLocaleDateString()
            case 'CURRENCY':
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }).format(Number(value))
            case 'NUMBER':
                return new Intl.NumberFormat('en-US').format(Number(value))
            case 'BOOLEAN':
                return value.toLowerCase() === 'true' ? 'Yes' : 'No'
            default:
                return value
        }
    }

    return (
        <Container
            style={{
                flexDirection: 'row',
                borderBottom: '1px solid #e0e0e0',
            }}
        >
            {/* Status column */}
            <Container
                style={{
                    ...CELL_STYLES.base,
                    minWidth: STATUS_COLUMN_WIDTH,
                    maxWidth: STATUS_COLUMN_WIDTH,
                    justifyContent: 'center',
                }}
            >
                <RowStatusBadge status={row.status} theme={theme} />
            </Container>

            {/* Required criteria columns */}
            {criteriaGroup?.match_criteria
                .filter((criteria) => criteria.display)
                .map((criteria) => {
                    const value = row.values.find(
                        (v) =>
                            v.entity_classification.id ===
                            criteria.entity_class.id,
                    )

                    return (
                        <DataCell
                            key={criteria.id}
                            theme={theme}
                            status={value?.status}
                            messageCount={value?.message_count}
                            value={value}
                            onClick={() => {
                                if (value) {
                                    onClickCell(value, row)
                                }
                            }}
                        >
                            <span>
                                {formatValueByDataType(
                                    value?.display_value || null,
                                    criteria.entity_class.data_type,
                                )}
                            </span>
                        </DataCell>
                    )
                })}
        </Container>
    )
}

// Original TableRow for backward compatibility
const TableRow = ({
    row,
    theme,
    criteriaGroup,
    onClickCell,
}: {
    row: ResidentTermDirectoryRow
    theme: Theme
    criteriaGroup: CriteriaGroup | undefined
    onClickCell: (cell: DeliverableValue, row: ResidentTermDirectoryRow) => void
}) => {
    return (
        <TableRowWithAlwaysDisplayIds
            row={row}
            theme={theme}
            criteriaGroup={criteriaGroup}
            onClickCell={onClickCell}
        />
    )
}

// Status Icon component for use in the detail panel
const StatusIcon = ({
    status,
    theme,
}: {
    status: DeliverableValueStatus
    theme: Theme
}) => {
    const statusConfig = DeliverableValueStatusConfig[status]
    const IconComponent = statusConfig.icon.component

    return (
        <Tooltip
            title={status
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/\b\w/g, (c) => c.toUpperCase())}
            placement="top"
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2px',
                    borderRadius: '50%',
                    backgroundColor:
                        status === 'NOT_MATCHED'
                            ? 'rgba(244, 67, 54, 0.1)'
                            : 'transparent',
                }}
            >
                <IconComponent
                    style={{
                        color: statusConfig.icon.color,
                        fontSize: '20px',
                    }}
                />
            </div>
        </Tooltip>
    )
}
